import type { Client, Thread } from "@langchain/langgraph-sdk";
import {
  infiniteQueryOptions,
  queryOptions,
  skipToken,
} from "@tanstack/react-query";

const PAGE_SIZE = 20;

export const getAgentThreadsQueryKey = (orgId: string, userId: string) =>
  ["agent-threads", orgId, userId] as const;

export const getAgentThreadsOptions = (
  client: Client,
  orgId: string,
  userId: string
) =>
  infiniteQueryOptions({
    queryKey: getAgentThreadsQueryKey(orgId, userId),
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      client.threads.search({
        metadata: { organization_id: orgId, user_id: userId },
        select: ["thread_id", "metadata", "updated_at", "created_at", "status"],
        limit: PAGE_SIZE,
        offset: pageParam,
        sortBy: "updated_at",
        sortOrder: "desc",
        signal,
      }),
    getNextPageParam: (lastPage, _pages, offset) =>
      lastPage.length === PAGE_SIZE ? offset + lastPage.length : undefined,
    enabled: !!orgId && !!userId,
    staleTime: 30_000,
    throwOnError: false,
  });

export const getAgentThreadOptions = (
  client: Client,
  orgId: string,
  threadId: string | null
) =>
  queryOptions({
    queryKey: ["agent-thread", orgId, threadId],
    queryFn: threadId ? () => client.threads.get(threadId) : skipToken,
    throwOnError: false,
    enabled: !!orgId && !!threadId,
    staleTime: 30_000,
  });

export const flattenAgentThreads = (pages: Thread[][] = []) =>
  Array.from(
    new Map(pages.flat().map((thread) => [thread.thread_id, thread])).values()
  );

export const getRoadmapThreadOptions = (
  client: Client,
  orgId: string,
  userId: string,
  taskContext: string
) =>
  queryOptions({
    queryKey: ["agent-roadmap-thread", orgId, userId, taskContext],
    queryFn: async () => {
      const threads = await client.threads.search({
        metadata: {
          organization_id: orgId,
          user_id: userId,
          roadmap_task: taskContext,
        },
        limit: 1,
        sortBy: "created_at",
        sortOrder: "desc",
      });
      return threads[0] ?? null;
    },
  });
